import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pause, Play, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Modal, Field, inputCls, useDirectory } from "./common";

const empty = { customer_id: "", property_id: "", employee_id: "", service_type: "Mowing", frequency: "weekly", start_date: "", color: "#4B5320", notes: "" };

export default function RecurringManager() {
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(null);
  const [props, setProps] = useState([]);
  const { customers, employees, custName } = useDirectory();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/recurring"); setItems(data); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (modal?.customer_id) api.get(`/properties?customer_id=${modal.customer_id}`).then(({ data }) => setProps(data)).catch(() => setProps([]));
  }, [modal?.customer_id]);

  const set = (k, v) => setModal((m) => ({ ...m, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...modal, start_date: modal.start_date ? new Date(modal.start_date).toISOString() : null };
      const { data } = await api.post("/recurring", payload);
      toast.success(`Recurring schedule created — ${data.generated_jobs} jobs added`); setModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const toggle = async (r) => { try { await api.put(`/recurring/${r.id}`, { paused: !r.paused }); load(); } catch (e) { toast.error("Failed"); } };
  const del = async (id) => { try { await api.delete(`/recurring/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <div data-testid="recurring-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-600 text-charcoal">Recurring Services</h2>
        <button onClick={() => setModal({ ...empty })} data-testid="new-recurring-btn" className="inline-flex items-center gap-2 bg-emerald text-white px-4 py-2.5 rounded-sm text-sm font-600 hover:bg-emerald-dark"><Plus className="w-4 h-4" /> New Recurring</button>
      </div>
      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-olive" /> : items.length === 0 ? (
        <p className="text-olive" data-testid="recurring-empty">No recurring schedules yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-border rounded-sm p-5 flex items-center justify-between gap-3" data-testid={`recurring-${r.id}`}>
              <div>
                <span className="font-600 text-charcoal">{custName(r.customer_id)} · {r.service_type}</span>
                <p className="text-olive text-sm capitalize">{r.frequency}{r.paused ? " · paused" : ""}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle(r)} data-testid={`recurring-toggle-${r.id}`} className="grid place-items-center w-9 h-9 rounded-sm text-charcoal/70 hover:bg-border">{r.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}</button>
                <button onClick={() => del(r.id)} data-testid={`recurring-delete-${r.id}`} className="grid place-items-center w-9 h-9 rounded-sm text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title="New Recurring Service" onClose={() => setModal(null)} testid="recurring-modal">
          <Field label="Customer"><select className={inputCls} data-testid="recurring-customer" value={modal.customer_id} onChange={(e) => set("customer_id", e.target.value)}><option value="">Select…</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}</select></Field>
          <Field label="Property"><select className={inputCls} value={modal.property_id} onChange={(e) => set("property_id", e.target.value)}><option value="">Select…</option>{props.map((p) => <option key={p.id} value={p.id}>{p.label} — {p.address}</option>)}</select></Field>
          <Field label="Employee"><select className={inputCls} value={modal.employee_id} onChange={(e) => set("employee_id", e.target.value)}><option value="">Unassigned</option>{employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name || emp.email}</option>)}</select></Field>
          <Field label="Service Type"><input className={inputCls} value={modal.service_type} onChange={(e) => set("service_type", e.target.value)} /></Field>
          <Field label="Frequency"><select className={inputCls} data-testid="recurring-frequency" value={modal.frequency} onChange={(e) => set("frequency", e.target.value)}>{["weekly", "biweekly", "monthly", "seasonal"].map((f) => <option key={f} value={f}>{f}</option>)}</select></Field>
          <Field label="Start Date & Time"><input type="datetime-local" className={inputCls} data-testid="recurring-start" value={modal.start_date} onChange={(e) => set("start_date", e.target.value)} /></Field>
          <button onClick={save} data-testid="recurring-save-btn" className="bg-emerald text-white px-5 py-2.5 rounded-sm font-600 hover:bg-emerald-dark">Save & Generate</button>
        </Modal>
      )}
    </div>
  );
}
